import { useNavigate } from 'react-router-dom';
import { ChevronRight, Paperclip } from 'lucide-react';
import { formatFileSize } from '@shared/lib/format';
import type { FeedAttachment } from './types';
import { MarqueeText } from '@shared/ui/primitives/MarqueeText';

/**
 * Reads `metadata.attachments` off a notification defensively — the column is freeform
 * jsonb shared with other notification producers, and messages sent before attachments
 * existed have none.
 */
export function readAttachments(metadata: unknown): FeedAttachment[] {
  const list = (metadata as { attachments?: unknown } | null)?.attachments;
  if (!Array.isArray(list)) return [];
  return list
    .filter((a): a is Record<string, unknown> => Boolean(a) && typeof a === 'object')
    .filter((a) => typeof a.document_id === 'string' && (a.document_id as string).length > 0)
    .map((a) => ({
      document_id: a.document_id as string,
      filename: typeof a.filename === 'string' && a.filename ? a.filename : 'Attachment',
      mime_type: typeof a.mime_type === 'string' ? a.mime_type : null,
      size_bytes: typeof a.size_bytes === 'number' ? a.size_bytes : null,
    }));
}

/**
 * Files attached to a message. A tap opens the file in the in-app viewer.
 *
 * No row here is a link to a document. The viewer asks the server for a fresh, short-lived
 * URL, and the server only mints one after confirming this message belongs to this member
 * and genuinely carries that file, so a member can never reach a document not sent to them.
 */
export function AttachmentList({
  tenantId,
  notificationId,
  attachments,
}: {
  tenantId: string | null;
  notificationId: string;
  attachments: FeedAttachment[];
}): JSX.Element | null {
  const navigate = useNavigate();
  if (attachments.length === 0) return null;

  const view = (a: FeedAttachment): void => {
    const params = new URLSearchParams({
      title: a.filename,
      file: a.filename,
      type: a.mime_type ?? '',
      size: String(a.size_bytes ?? ''),
    });
    // A message from another linked community is fetched through that community.
    if (tenantId) params.set('tenantId', tenantId);
    navigate(`/app/communication/messages/${notificationId}/attachments/${a.document_id}/view?${params.toString()}`);
  };

  return (
    <div className="mt-4 rounded-lg border border-hairline bg-card p-3">
      <p className="text-[0.9375rem] font-bold text-ink">
        {attachments.length === 1 ? 'Attachment' : `Attachments (${attachments.length})`}
      </p>
      <ul className="mt-2 flex flex-col gap-1">
        {attachments.map((a) => (
          <li key={a.document_id}>
            <button
              type="button"
              onClick={() => view(a)}
              className="flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-2 text-left text-body-sm text-ink hover:bg-sunken"
            >
              <Paperclip className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
              <MarqueeText className="min-w-0 flex-1">{a.filename}</MarqueeText>
              {a.size_bytes ? (
                <span className="shrink-0 text-caption text-ink-muted">{formatFileSize(a.size_bytes)}</span>
              ) : null}
              <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
